/**
 * Client Dashboard — simple, non-accountant view of financial status.
 * All data comes from existing APIs. Read-only. No accounting jargon.
 */
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useBusinesses } from "@/hooks/use-businesses";
import { apiRequest } from "@/lib/queryClient";
import { useLocation } from "wouter";
import {
  CheckCircle, AlertTriangle, Clock, TrendingUp, TrendingDown,
  DollarSign, FileText, ArrowRight, Brain, Lock, Unlock, BarChart3
} from "lucide-react";

const fmt = (n: number | null | undefined) =>
  (n ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

export default function ClientDashboardPage() {
  const { activeBusiness } = useBusinesses();
  const [, navigate] = useLocation();
  const businessId = activeBusiness?.id;

  // Period lock status
  const { data: lockInfo } = useQuery({
    queryKey: ["/client-dash/lock", businessId],
    queryFn: async () => {
      const r = await apiRequest("GET", `/api/businesses/${businessId}/period-lock`);
      return r.json();
    },
    enabled: !!businessId,
  });

  // Latest snapshot (immutable financial state)
  const { data: snapshot, isLoading: snapLoading } = useQuery({
    queryKey: ["/client-dash/snapshot", businessId],
    queryFn: async () => {
      const r = await apiRequest("GET", `/api/businesses/${businessId}/snapshots/latest`);
      return r.json();
    },
    enabled: !!businessId,
  });

  // No AI fallback — compliance requirement: only snapshot data

  // Pending approvals
  const { data: pendingActions = [] } = useQuery({
    queryKey: ["/client-dash/pending", businessId],
    queryFn: async () => {
      const r = await apiRequest("GET", `/api/ai-agent/queue?status=pending`);
      return r.json();
    },
    enabled: !!businessId,
    refetchInterval: 30000,
  });

  // Categorization stats
  const { data: catStats } = useQuery({
    queryKey: ["/client-dash/cat-stats", businessId],
    queryFn: async () => {
      const r = await apiRequest("GET", `/api/businesses/${businessId}/categorization-stats`);
      return r.json();
    },
    enabled: !!businessId,
  });

  // Latest workflow
  const { data: workflows = [] } = useQuery({
    queryKey: ["/client-dash/workflows", businessId],
    queryFn: async () => {
      const r = await apiRequest("GET", `/api/ai-agent/workflows`);
      return r.json();
    },
    enabled: !!businessId,
  });

  // Derive financial numbers ONLY from snapshot (compliance)
  const hasSnapshot = snapshot != null;
  const pnl = snapshot?.pnl_json;
  const revenue = pnl?.totalRevenue ?? 0;
  const expenses = pnl?.totalExpenses ?? 0;
  const netIncome = pnl?.netIncome ?? 0;
  const uncategorized = catStats?.uncategorized ?? 0;

  // Snapshot metadata
  const snapshotDate = snapshot?.created_at ? new Date(snapshot.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : null;
  const periodLabel = snapshot?.period_start && snapshot?.period_end
    ? `${new Date(snapshot.period_start).toLocaleDateString("en-US", { month: "short", year: "numeric" })} — ${new Date(snapshot.period_end).toLocaleDateString("en-US", { month: "short", year: "numeric" })}`
    : null;

  // Status derivation — hardened definition
  const isLocked = lockInfo?.locked === true;
  const lockDate = lockInfo?.lockDate ? new Date(lockInfo.lockDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : null;
  const booksReady = isLocked && pendingActions.length === 0;
  const hasIssues = !booksReady && (uncategorized > 0 || pendingActions.length > 0);

  // No AI-derived insights on client dashboard (compliance)
  const insights: any[] = [];
  const suggestions: any[] = [];

  if (!businessId) {
    return <Layout><div className="p-8 text-center text-muted-foreground">Select a business to view your dashboard.</div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-primary/10 rounded-xl"><BarChart3 className="h-7 w-7 text-primary" /></div>
          <div>
            <h1 className="text-2xl font-bold">Dashboard</h1>
            <p className="text-muted-foreground text-sm">{activeBusiness?.name}</p>
          </div>
        </div>

        {/* 1️⃣ Status Card */}
        <Card className={booksReady ? "border-green-200" : hasIssues ? "border-yellow-200" : ""}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${booksReady ? "bg-green-100" : hasIssues ? "bg-yellow-100" : "bg-blue-100"}`}>
                {booksReady ? <CheckCircle className="h-6 w-6 text-green-600" /> :
                 hasIssues ? <AlertTriangle className="h-6 w-6 text-yellow-600" /> :
                 <Clock className="h-6 w-6 text-blue-600" />}
              </div>
              <div className="flex-1">
                <div className="text-lg font-semibold">
                  {booksReady ? "Books are up to date" : hasIssues ? "Items need your attention" : "Work in progress"}
                </div>
                <div className="text-sm text-muted-foreground">
                  {isLocked ? (
                    <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> Locked through {lockDate}</span>
                  ) : (
                    <span className="flex items-center gap-1"><Unlock className="h-3 w-3" /> Period is open</span>
                  )}
                </div>
              </div>
              <Badge className={booksReady ? "bg-green-100 text-green-800" : hasIssues ? "bg-yellow-100 text-yellow-800" : "bg-blue-100 text-blue-800"}>
                {booksReady ? "Ready" : hasIssues ? "Needs Review" : "In Progress"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* 2️⃣ Alerts */}
        {hasIssues && (
          <div className="space-y-2">
            {pendingActions.length > 0 && (
              <Alert>
                <Brain className="h-4 w-4" />
                <AlertDescription className="flex items-center justify-between">
                  <span>{pendingActions.length} item{pendingActions.length > 1 ? "s" : ""} need your approval</span>
                  <Button size="sm" variant="outline" onClick={() => navigate("/ai-bookkeeper")} className="gap-1">Review <ArrowRight className="h-3 w-3" /></Button>
                </AlertDescription>
              </Alert>
            )}
            {uncategorized > 0 && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{uncategorized} transaction{uncategorized > 1 ? "s" : ""} still need{uncategorized === 1 ? "s" : ""} to be categorized</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* 3️⃣ Financial Snapshot */}
        {snapLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
          </div>
        ) : !hasSnapshot ? (
          <Card className="border-dashed">
            <CardContent className="p-10 text-center">
              <div className="bg-muted/50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-8 w-8 text-muted-foreground/50" />
              </div>
              <h3 className="font-semibold text-lg mb-1">No financial snapshot yet</h3>
              <p className="text-muted-foreground text-sm mb-5 max-w-sm mx-auto">
                Financials will appear once your books are finalized. Get started by connecting your bank or uploading documents.
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={() => navigate("/transactions")} className="gap-2">
                  <DollarSign className="h-4 w-4" /> Connect Bank
                </Button>
                <Button variant="outline" onClick={() => navigate("/bookkeeping/receipts")} className="gap-2">
                  <FileText className="h-4 w-4" /> Upload Documents
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {(periodLabel || snapshotDate) && (
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                {periodLabel && <span className="font-medium">{periodLabel}</span>}
                {snapshotDate && <span>Snapshot taken {snapshotDate}</span>}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <TrendingUp className="h-4 w-4 text-green-500" /> Money In
                </div>
                <div className="text-2xl font-bold text-green-600">{fmt(revenue)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <TrendingDown className="h-4 w-4 text-red-500" /> Money Out
                </div>
                <div className="text-2xl font-bold text-red-600">{fmt(expenses)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="text-muted-foreground text-sm mb-1">Profit / Loss</div>
                <div className={`text-2xl font-bold ${netIncome >= 0 ? "text-green-600" : "text-red-600"}`}>{fmt(netIncome)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="text-muted-foreground text-sm mb-1">Needs Review</div>
                <div className={`text-2xl font-bold ${uncategorized > 0 ? "text-yellow-600" : "text-green-600"}`}>
                  {uncategorized} item{uncategorized !== 1 ? "s" : ""}
                </div>
              </CardContent>
            </Card>
          </div>
          </div>
        )}

        {/* 4️⃣ Insights */}
        {insights.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">What You Should Know</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {insights.slice(0, 4).map((insight: any, i: number) => (
                <div key={i} className={`p-3 rounded-lg border text-sm ${
                  insight.type === "warning" ? "bg-yellow-50 border-yellow-200" :
                  insight.type === "positive" ? "bg-green-50 border-green-200" :
                  "bg-blue-50 border-blue-200"
                }`}>
                  {insight.type === "warning" ? "⚠️" : insight.type === "positive" ? "✅" : "💡"} {insight.message}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* 5️⃣ Action Buttons */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => navigate("/reports/trial-balance")} className="gap-2">
              <FileText className="h-4 w-4" /> View Reports
            </Button>
            <Button variant="outline" onClick={() => navigate("/transactions")} className="gap-2">
              <DollarSign className="h-4 w-4" /> Review Transactions
            </Button>
            {pendingActions.length > 0 && (
              <Button onClick={() => navigate("/ai-bookkeeper")} className="gap-2">
                <Brain className="h-4 w-4" /> Approve Items
                <Badge variant="destructive" className="ml-1">{pendingActions.length}</Badge>
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Suggestions</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {suggestions.slice(0, 3).map((s: any, i: number) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                  <span className="text-sm">{s.description}</span>
                  <Badge variant={s.priority === "high" ? "destructive" : "secondary"}>{s.priority}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
